<template lang="pug">
div
    h3.title.is-5 Push Notifications Debug
    
    .my-4
      p Service Worker Status
      table.table.is-fullwidth
        tr
          th Service Worker Supported
          td 
            span.tag(:class="isSupported ? 'is-success' : 'is-danger'") {{ isSupported ? 'Yes' : 'No' }}
        tr
          th Push API Supported
          td 
            span.tag(:class="isPushSupported ? 'is-success' : 'is-danger'") {{ isPushSupported ? 'Yes' : 'No' }}
        tr
          th Notification Permission
          td 
            span.tag(:class="permissionClass") {{ permissionStatus }}
        tr
          th Service Worker Registration
          td 
            span.tag(:class="swRegistration ? 'is-success' : 'is-danger'") {{ swRegistration ? 'Registered' : 'Not Registered' }}
        tr
          th Push Subscription
          td 
            span.tag(:class="isSubscribed ? 'is-success' : 'is-danger'") {{ isSubscribed ? 'Subscribed' : 'Not Subscribed' }}
    
    .buttons
      button.button.is-primary(@click="registerServiceWorker" :disabled="swRegistration") Register Service Worker
      button.button.is-info(@click="requestPermission" :disabled="permissionStatus === 'granted'") Request Permission
      button.button.is-success(@click="subscribe" :disabled="!canSubscribe") Subscribe
      button.button.is-warning(@click="unsubscribe" :disabled="!isSubscribed") Unsubscribe
      button.button.is-info.is-light(@click="checkRegistration") Refresh Status
    
    .notification.is-info.is-light.mt-3(v-if="subscriptionInfo")
      p Subscription Info:
      pre.has-text-left {{ subscriptionInfo }}
    
    .notification.is-danger.is-light.mt-3(v-if="error")
      button.delete(@click="error = null")
      p {{ error }}

</template>

<script>
import {
  isPushSupported,
  checkPushSubscription,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  getNotificationPermission,
  requestNotificationPermission
} from '@/utils/pushNotifications'

export default {
  data() {
    return {
      isSupported: false,
      isPushSupported: false,
      permissionStatus: 'default',
      swRegistration: null,
      isSubscribed: false,
      isLoading: false,
      error: null,
      subscriptionInfo: null
    }
  },
  computed: {
    canSubscribe() {
      return (
        this.isSupported &&
        this.isPushSupported &&
        this.permissionStatus === 'granted' &&
        this.swRegistration &&
        !this.isSubscribed
      )
    },
    permissionClass() {
      if (this.permissionStatus === 'granted') return 'is-success'
      if (this.permissionStatus === 'denied') return 'is-danger'
      return 'is-warning'
    }
  },
  async mounted() {
    this.isSupported = 'serviceWorker' in navigator
    this.isPushSupported = isPushSupported()

    await this.checkRegistration()
  },
  methods: {
    async checkRegistration() {
      try {
        this.error = null

        // Check permission status
        const permissionResult = getNotificationPermission()
        this.permissionStatus = permissionResult.permission

        // Check service worker registration
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations()
          this.swRegistration = registrations.length > 0

          if (this.swRegistration) {
            // Check subscription status
            const subscriptionStatus = await checkPushSubscription()
            this.isSubscribed = subscriptionStatus.subscribed

            if (subscriptionStatus.subscribed && subscriptionStatus.subscription) {
              this.subscriptionInfo = JSON.stringify(subscriptionStatus.subscription, null, 2)
            } else {
              this.subscriptionInfo = null
            }
          }
        }
      } catch (error) {
        console.error('Error checking status:', error)
        this.error = error.message || 'Failed to check status'
      }
    },

    async registerServiceWorker() {
      try {
        this.error = null

        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.register(`${process.env.BASE_URL || '/'}sworker.js`)
          console.log('ServiceWorker registration successful with scope:', registration.scope)
          this.swRegistration = true
          await this.checkRegistration()
        }
      } catch (error) {
        console.error('Error registering service worker:', error)
        this.error = error.message || 'Failed to register service worker'
      }
    },

    async requestPermission() {
      try {
        this.error = null

        const result = await requestNotificationPermission()
        if (result.granted) {
          this.permissionStatus = 'granted'
        } else {
          this.error = `Permission not granted: ${result.reason || 'unknown reason'}`
        }

        await this.checkRegistration()
      } catch (error) {
        console.error('Error requesting permission:', error)
        this.error = error.message || 'Failed to request permission'
      }
    },

    async subscribe() {
      try {
        this.error = null
        this.isLoading = true

        await subscribeToPushNotifications()

        await this.checkRegistration()
      } catch (error) {
        console.error('Error subscribing:', error)
        this.error = error.message || 'Failed to subscribe'
      } finally {
        this.isLoading = false
      }
    },

    async unsubscribe() {
      try {
        this.error = null
        this.isLoading = true

        const result = await unsubscribeFromPushNotifications()

        if (!result.success) {
          throw new Error(result.error || 'Failed to unsubscribe')
        }

        this.subscriptionInfo = null
        await this.checkRegistration()
      } catch (error) {
        console.error('Error unsubscribing:', error)
        this.error = error.message || 'Failed to unsubscribe'
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>
